// Panel platform terpisah (`/platform/admin`) — helper MURNI, tanpa I/O.
//
// Seluruh keputusan routing/otorisasi panel platform tinggal di sini sebagai
// fungsi murni; `src/middleware.ts` (cabang host platform) dan layout
// `/platform/admin` hanya memanggil. Pola yang sama dengan `loginStoreGate.ts`:
// matriks keputusannya bisa diuji tanpa HTTP. Diuji di
// `__tests__/platformAdmin.test.ts`.
//
// KENAPA PATH TERPISAH (bukan `/admin` ber-cabang Host): proxy Hostinger
// (hcdn/LiteSpeed) MENGABAIKAN `Vary`, jadi satu path yang menyajikan dua
// aplikasi berbeda tergantung Host adalah bentuk persis bug peracunan cache
// yang pernah menggigit project ini. Path berbeda mematikan seluruh kelas bug
// itu. `kelolify.com/admin` tetap jadi pintu masuk — di-302 ke sini.

export const PLATFORM_ADMIN_PREFIX = "/platform/admin";
export const PLATFORM_LOGIN_PATH = "/platform/login";

export type PlatformAccess =
  | { kind: "allow" }
  | { kind: "toLogin"; next: string }
  | { kind: "toPlatformAdmin" }
  | { kind: "denyNotOwner" } // dirender sebagai halaman oleh layout, BUKAN redirect
  | { kind: "notFound" };

/** `/platform/admin` beserta seluruh sub-path-nya (hormati batas segmen). */
export function isPlatformAdminPath(pathname: string): boolean {
  return (
    pathname === PLATFORM_ADMIN_PREFIX ||
    pathname.startsWith(`${PLATFORM_ADMIN_PREFIX}/`)
  );
}

/** Area platform yang butuh keputusan sesi: panel admin + halaman login-nya. */
export function isPlatformAuthPath(pathname: string): boolean {
  return isPlatformAdminPath(pathname) || pathname === PLATFORM_LOGIN_PATH;
}

/**
 * Guard open-redirect untuk parameter `next` halaman login platform: hanya
 * path INTERNAL area platform (diawali "/platform/") yang boleh diteruskan.
 * `//jahat.com` (protocol-relative) dan URL absolut otomatis tertolak karena
 * tidak berawalan itu. Selain itu → default panel.
 */
export function safePlatformNext(raw: string | null | undefined): string {
  const value = (raw ?? "").trim();
  if (!value.startsWith("/platform/")) return PLATFORM_ADMIN_PREFIX;
  return value;
}

/**
 * Matriks akses area platform. `denyNotOwner` SENGAJA bukan redirect ke login:
 * akun itu sudah login dengan benar, ia hanya bukan pemilik platform —
 * melemparnya ke login menghasilkan lingkaran dan pesan yang membingungkan.
 * Host bukan-platform → `notFound` (keberadaan panel tidak diumumkan ke
 * domain penyewa; 404, bukan redirect).
 */
export function resolvePlatformAccess(input: {
  isPlatformHost: boolean;
  pathname: string;
  hasSession: boolean;
  isPlatformOwner: boolean;
}): PlatformAccess {
  if (!input.isPlatformHost) return { kind: "notFound" };

  if (input.pathname === PLATFORM_LOGIN_PATH) {
    // Sudah login sebagai pemilik → tak ada gunanya form login. Sesi non-owner
    // dibiarkan melihat form (ganti akun) — penolakannya di server login.
    return input.hasSession && input.isPlatformOwner
      ? { kind: "toPlatformAdmin" }
      : { kind: "allow" };
  }

  if (isPlatformAdminPath(input.pathname)) {
    if (!input.hasSession) {
      return { kind: "toLogin", next: safePlatformNext(input.pathname) };
    }
    if (!input.isPlatformOwner) return { kind: "denyNotOwner" };
    return { kind: "allow" };
  }

  // Di luar area terautentikasi platform, helper ini tak berwenang memutuskan
  // apa pun — fail-closed (pemanggil memang tidak boleh sampai ke sini).
  return { kind: "notFound" };
}
